import React from 'react'

type FindWordIndexOptions = {
  caseSensitive: boolean
  wholeWord: boolean
}

/**
 * Finds the index of a word in a string
 * @param {string} str - The string to search in
 * @param {string} word - The word to find
 * @param {Object} options - Additional options
 * @param {boolean} options.caseSensitive - Whether the search should be case sensitive (default: true)
 * @param {boolean} options.wholeWord - Whether to match whole words only (default: true)
 * @returns {number} The index of the word, or -1 if not found
 */
export const findWordIndex = (str, word, options = {}) => {
  if (!str || !word) return -1

  const {
    caseSensitive = true,
    wholeWord = true,
  } = options as FindWordIndexOptions

  const searchStr = caseSensitive ? str : str.toLowerCase()
  const searchWord = caseSensitive ? word : word.toLowerCase()

  if (wholeWord) {
    // Match whole words only
    const regex = new RegExp(`\\b${searchWord}\\b`, 'g')
    const match = regex.exec(searchStr)
    return match ? match.index : -1
  }

  // Match any occurrence
  return searchStr.indexOf(searchWord)
}

/**
 * Finds all indices of a word in a string
 * @param {string} str - The string to search in
 * @param {string} word - The word to find
 * @param {Object} options - Additional options
 * @param {boolean} options.caseSensitive - Whether the search should be case sensitive (default: true)
 * @param {boolean} options.wholeWord - Whether to match whole words only (default: true)
 * @returns {number[]} Array of indices where the word was found
 */
export const findAllWordIndices = (str, word, options = {}) => {
  if (!str || !word) return []

  const {
    caseSensitive = true,
    wholeWord = true,
  } = options as FindWordIndexOptions

  const searchStr = caseSensitive ? str : str.toLowerCase()
  const searchWord = caseSensitive ? word : word.toLowerCase()
  const indices = []

  if (wholeWord) {
    // Match whole words only
    const regex = new RegExp(`\\b${searchWord}\\b`, 'g')
    let match
    // eslint-disable-next-line no-cond-assign
    while ((match = regex.exec(searchStr)) !== null) {
      indices.push(match.index)
    }
  } else {
    // Match any occurrence
    let index = searchStr.indexOf(searchWord)
    // eslint-disable-next-line no-cond-assign
    while (index !== -1) {
      indices.push(index)
      index = searchStr.indexOf(searchWord, index + 1)
    }
  }

  return indices
}

/**
 * Wraps target words in a string with JSX bold elements
 * @param {string} str - The original string
 * @param {string|string[]} targetWords - The word(s) to make bold
 * @param {Object} options - Additional options
 * @param {boolean} options.caseSensitive - Whether the search should be case sensitive (default: true)
 * @param {boolean} options.wholeWord - Whether to match whole words only (default: true)
 * @returns {React.ReactNode} The string with target words wrapped in bold components
 */
export const makeWordsBold = (str, targetWords, options = {}) => {
  // Convert input to string if it's not already
  const inputStr = String(str || '')
  if (!inputStr || !targetWords) return inputStr

  const {
    caseSensitive = true,
    wholeWord = true,
  } = options as FindWordIndexOptions

  // Convert single word to array and ensure all words are strings
  const words = (Array.isArray(targetWords) ? targetWords : [targetWords])
    .map(word => String(word || ''))
    .filter(word => word.length > 0)

  if (words.length === 0) return inputStr

  // Create a copy of the string to modify
  const result = inputStr
  const parts = []
  let lastIndex = 0

  // Create a single regex pattern for all words
  const pattern = words
    .map(word => {
      const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return wholeWord ? `\\b${escapedWord}\\b` : escapedWord
    })
    .join('|')

  const regex = new RegExp(pattern, caseSensitive ? 'g' : 'gi')
  let match

  // eslint-disable-next-line no-cond-assign
  while ((match = regex.exec(result)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(result.slice(lastIndex, match.index))
    }
    // Add the bold word
    parts.push(
      <h3 key={`${match.index}-${match[0]}`}>
        {match[0]}
      </h3>,
    )
    lastIndex = match.index + match[0].length
  }

  // Add remaining text
  if (lastIndex < result.length) {
    parts.push(result.slice(lastIndex))
  }

  return parts.length > 0 ? parts : result
}

/**
 * Updates a URL by replacing path parameters with actual values
 * @param {Object} params - The parameters object
 * @param {string} params.url - The original URL containing path parameters
 * @param {string} [params.email] - The email to replace /:email with
 * @param {string} [params.requestId] - The request ID to replace /:requestId with
 * @param {string} [params.userId] - The user ID to replace /:userId with
 * @returns {string} The updated URL with replaced values
 */
export const updateUrl = ({
  url,
  email,
  requestId,
  userId,
}) => {
  if (!url) return url

  let updatedUrl = url

  if (email) {
    updatedUrl = updatedUrl.replace('/:email', `/${email}`)
  }

  if (requestId) {
    updatedUrl = updatedUrl.replace('/:requestId', `/${requestId}`)
  }

  if (userId) {
    updatedUrl = updatedUrl.replace('/:userId', `/${userId}`)
  }

  return updatedUrl
}

/**
 * Capitalize the first letter
 * @param value
 * @return string
 * */
export const capitalize = value => {
  if (!value || typeof value !== 'string') return value
  return value.charAt(0).toUpperCase() + value.slice(1)
}

type SanitizeApplicationSlugOptions = {
  // Live typing keeps a trailing hyphen so "onboarding-" can become "onboarding-espana",
  // and keeps dead-key accents (¨) so Option+u then o can become ö.
  // Save/paste strip both.
  allowTrailingHyphen?: boolean
}

/**
 * Application URL slug: lowercase, spaces/dashes to a single hyphen, letters
 * in any script (so "españa" stays "españa"), digits. Other punctuation is
 * stripped. Trailing hyphens are kept while typing and removed on save.
 *
 * Unicode property escapes must be built with String.raw. Next/SWC rewrites
 * regex literals like /\p{L}/u into /p{L}/u, which strips ñ.
 */
const SLUG_DASH_OR_SPACE = new RegExp(String.raw`[\p{Pd}\s]+`, 'gu')
const NOT_SLUG_CHAR = new RegExp(String.raw`[^\p{L}\p{N}\p{M}-]`, 'gu')
// \p{Sk} is the spacing accent macOS inserts for Option+u / Option+n (¨, ˜)
// before the next letter composes into ö / ñ. Stripping it on each keystroke
// makes the umlaut never land in the slug.
const NOT_LIVE_SLUG_CHAR = new RegExp(String.raw`[^\p{L}\p{N}\p{M}\p{Sk}-]`, 'gu')

export function sanitizeApplicationSlug(str, options: SanitizeApplicationSlugOptions = {}) {
  if (typeof str !== 'string') return ''

  const strip = options.allowTrailingHyphen ? NOT_LIVE_SLUG_CHAR : NOT_SLUG_CHAR

  let result = str
    .normalize('NFC')
    .toLowerCase()
    .replace(SLUG_DASH_OR_SPACE, '-')
    .replace(strip, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/g, '')

  if (!options.allowTrailingHyphen) {
    result = result.replace(/-+$/g, '')
  }

  return result
}

/**
 * Letters (any script), digits, combining marks, dash punctuation (hyphen,
 * en-dash, em-dash), dead-key accents, and spaces. Shared by the keydown
 * guard and the paste/onChange sanitizer so display names stay in sync.
 *
 * The URL-safe slug is handled separately, so workflow / application /
 * company display names can use international characters and dashes.
 */
const ALLOWED_DISPLAY_NAME_CHAR = new RegExp(String.raw`^[\p{L}\p{N}\p{M}\p{Pd}\p{Sk} ]$`, 'u')
const DISALLOWED_DISPLAY_NAME_CHARS = new RegExp(String.raw`[^\p{L}\p{N}\p{M}\p{Pd}\p{Sk} ]`, 'gu')
const CONSECUTIVE_DISPLAY_DASHES = new RegExp(String.raw`\p{Pd}{2,}`, 'gu')
const LEADING_DISPLAY_DASHES = new RegExp(String.raw`^\p{Pd}+`, 'gu')
const HAS_LETTER_OR_NUMBER = new RegExp(String.raw`[\p{L}\p{N}]`, 'u')

/**
 * Prevent disruptive characters from being typed in `onKeyDown`.
 * Allows unicode letters, digits, combining marks, and dashes (supports
 * names like "Onboarding España" and "Onboarding – California") while
 * blocking other punctuation/symbols and consecutive spaces.
 *
 * Modifier and composing keys are left alone so dead-key / Option /
 * IME sequences can produce accented letters; `removeSpecialCharacters`
 * still sanitizes the committed value.
 *
 * @param {event} event
 * @returns string
 */
export const preventSpecialCharacters = event => {
  const char = event.key
  const currentValue = event.target.value
  const cursorPosition = event.target.selectionStart

  if (event.ctrlKey || event.metaKey || event.altKey || event.isComposing) {
    return
  }

  // Allow control keys (backspace, delete, arrows, etc.)
  if (event.key.length > 1 && !event.key.match(/^[A-Z]$/)) {
    return
  }

  if (!ALLOWED_DISPLAY_NAME_CHAR.test(char)) {
    event.preventDefault()
    return
  }

  // Prevent space if previous character is already a space or at start
  if (char === ' ') {
    const prevChar = currentValue[cursorPosition - 1]
    if (prevChar === ' ' || cursorPosition === 0) {
      event.preventDefault()
    }
  }
}

/**
 * Removes disruptive characters from a string, keeping unicode letters,
 * digits, combining marks, dashes, and single spaces. Strips other
 * punctuation/symbols, collapses consecutive spaces and dashes, and
 * strips leading dashes so a dash-only name cannot be submitted.
 *
 * Trailing dashes stay typable so hyphenated names like
 * "Onboarding-California" can be entered without a space.
 *
 * @param {string} string
 * @returns string
 */
export const removeSpecialCharacters = string => string
  .replace(DISALLOWED_DISPLAY_NAME_CHARS, '')
  .replace(/\s{2,}/g, ' ')
  .replace(CONSECUTIVE_DISPLAY_DASHES, '-')
  .replace(LEADING_DISPLAY_DASHES, '')

/**
 * True when a display name has at least one letter or digit. Used to
 * disable create/rename while the field is empty, whitespace, or dashes.
 */
export const hasDisplayNameContent = (name: string | null | undefined) => (
  typeof name === 'string' && HAS_LETTER_OR_NUMBER.test(name)
)
